// src/pages/OCRTool/OCRToolPage.tsx
// Clean, Google-inspired OCR Tool with SEO optimization
import React, { useState, useRef, ChangeEvent } from 'react';
import { saveAs } from 'file-saver';
import SEO from '../../../components/SEO/SEO';
import { ToolSchema, BreadcrumbSchema, HowToSchema } from '../../../components/Schema';
import { seoConfig } from '../../../config/seo';
import {
    CheckCircle, Shield, Zap, Type,
    Upload, Copy, Download, Globe,
    FileText, Scan, RefreshCw
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import {
    ToolPageLayout,
    ToolInfoStrip,
    ToolSection,
    ToolStepCard,
    ToolFeatureCard,
    ToolFaqCta,
    ToolRelatedCard,
} from '../components';
import SecurityBadge from '../../../components/Shared/SecurityBadge/SecurityBadge';
import { toast } from 'react-toastify';

interface LanguageOption {
    value: string;
    label: string;
}

interface TesseractMessage {
    status: string;
    progress: number;
}

const OCRToolPage: React.FC = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [extractedText, setExtractedText] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const [selectedLanguage, setSelectedLanguage] = useState<string>('eng');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const languageOptions: LanguageOption[] = [
        { value: 'eng', label: 'English' },
        { value: 'fra', label: 'French' },
        { value: 'deu', label: 'German' },
        { value: 'spa', label: 'Spanish' },
        { value: 'ita', label: 'Italian' },
        { value: 'por', label: 'Portuguese' },
        { value: 'jpn', label: 'Japanese' },
        { value: 'kor', label: 'Korean' },
        { value: 'chi_sim', label: 'Chinese (Simplified)' },
        { value: 'chi_tra', label: 'Chinese (Traditional)' },
        { value: 'rus', label: 'Russian' },
        { value: 'ara', label: 'Arabic' },
        { value: 'hin', label: 'Hindi' }
    ];

    const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('File size exceeds 10MB limit');
                return;
            }
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
            setExtractedText('');
            setError(null);
        }
    };

    const handleExtractText = async () => {
        if (!selectedFile) {
            toast.error('Please select an image or PDF file');
            return;
        }

        setIsProcessing(true);
        setError(null);
        setProgress(0);

        try {
            // @ts-ignore
            const { default: Tesseract } = await import('tesseract.js');
            const result = await Tesseract.recognize(
                selectedFile,
                selectedLanguage,
                {
                    logger: (m: TesseractMessage) => {
                        if (m.status === 'recognizing text') {
                            setProgress(Math.round(m.progress * 100));
                        }
                    }
                }
            );

            if (result.data.text) {
                setExtractedText(result.data.text);
                toast.success('Text extracted successfully!');
            } else {
                setError('No text found in image');
                toast.warn('No text identified.');
            }
        } catch (err: any) {
            console.error(err);
            setError('Failed to extract text: ' + err.message);
            toast.error('Extraction failed. See details below.');
        } finally {
            setIsProcessing(false);
            setProgress(0);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(extractedText).then(() => {
            toast.success('Text copied to clipboard!');
        });
    };

    const downloadText = () => {
        const blob = new Blob([extractedText], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, 'extracted-text.txt');
        toast.success('Download started');
    };

    const handleReset = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setExtractedText('');
        setError(null);
        setProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        toast.info('Tool reset');
    };

    return (
        <>
            <SEO {...seoConfig.ocr} />
            <ToolSchema
                name="OCR Text Extractor"
                description="Free online OCR tool to extract text from images and PDFs. Supports 12+ languages with high accuracy AI-powered recognition."
                url="/tools/scan-image-to-text"
                applicationCategory="UtilitiesApplication"
                featureList={[
                    "Extract text from images",
                    "Extract text from PDF files",
                    "Support for 12+ languages",
                    "AI-powered recognition",
                    "Handwriting recognition",
                    "Copy or download results"
                ]}
            />
            <BreadcrumbSchema
                breadcrumbs={[
                    { name: 'Home', url: 'https://www.filefusion.app/' },
                    { name: 'Tools', url: 'https://www.filefusion.app/tools' },
                    { name: 'OCR Text Extractor', url: 'https://www.filefusion.app/tools/scan-image-to-text' }
                ]}
            />
            <HowToSchema
                name="How to Extract Text from Images Using OCR"
                description="Convert images and PDFs to editable text using optical character recognition technology."
                totalTime="PT3M"
                steps={[
                    { name: "Upload Your File", text: "Click or drag and drop an image or PDF containing text you want to extract." },
                    { name: "Select Language", text: "Choose the language of the text for better accuracy (optional)." },
                    { name: "Start Extraction", text: "Click the extract button and wait for the OCR processing to complete." },
                    { name: "Copy or Download", text: "Copy the extracted text to clipboard or download it as a text file." }
                ]}
                tool={["Web Browser", "Image or PDF file"]}
            />

            <ToolPageLayout
                icon={<Type size={32} strokeWidth={1.5} />}
                title="Free OCR Text Extractor"
                subtitle="Extract text from images and PDF documents with high accuracy. Supports over 100 languages with AI-powered recognition."
                tags={(
                    <>
                        <SecurityBadge type="local" />
                        <SecurityBadge type="private" />
                    </>
                )}
            >
                <div className="flex-1 min-w-0">
                    {error && (
                        <ErrorMessage
                            type="unknown"
                            message={error}
                            onClose={() => setError(null)}
                        />
                    )}

                    <div className="bg-[#ffffff] border-[1px] border-[#dadce0] rounded-[12px] p-[32px] shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)]">
                        <div className="mb-[24px]">
                            <h2 className="text-[1.375rem] font-normal text-[#202124] m-0 mb-[8px] text-center">Upload Image or PDF</h2>
                            <p className="text-[0.9375rem] text-[#5f6368] m-0 mb-[24px] text-center leading-[1.6]">
                                Select an image or PDF file containing text. We'll extract and convert it to
                                editable text using advanced OCR technology.
                            </p>

                            <div
                                className="border-[2px] border-dashed border-[#dadce0] rounded-[12px] py-[40px] px-[20px] bg-[#f8f9fa] cursor-pointer transition-all duration-200 min-h-[280px] flex flex-col items-center justify-center mb-[24px] hover:border-[#1a73e8] hover:bg-[#e8f0fe]"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {previewUrl ? (
                                    <div className="flex flex-col items-center gap-[16px] w-full">
                                        <img src={previewUrl} alt="Preview" className="max-w-full max-h-[200px] rounded-[8px] object-contain" />
                                        <div className="flex items-center gap-[8px] text-[#1e8e3e] text-[0.9375rem] font-medium">
                                            <CheckCircle size={18} />
                                            <span>{selectedFile?.name}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center text-center gap-[12px] [&>svg]:text-[#80868b] [&>svg]:mb-[8px] [&>p]:text-[1rem] [&>p]:text-[#202124] [&>p]:m-0 [&>span]:text-[0.875rem] [&>span]:text-[#80868b]">
                                        <Upload size={48} />
                                        <p>Click to upload or drag and drop</p>
                                        <span>JPG, PNG, GIF, WebP, PDF (Max 10MB)</span>
                                    </div>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,.pdf"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                            </div>

                            <div className="mb-[24px] flex flex-col items-center gap-[8px]">
                                <label htmlFor="language-select" className="flex items-center gap-[6px] text-[0.9375rem] text-[#5f6368] font-medium">
                                    <Globe size={18} />
                                    Select Text Language
                                </label>
                                <select
                                    id="language-select"
                                    value={selectedLanguage}
                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                    className="w-full max-w-[300px] py-[10px] px-[12px] border-[1px] border-[#dadce0] rounded-[4px] text-[1rem] text-[#202124] bg-[#ffffff] outline-none font-inherit focus:border-[#1a73e8] focus:shadow-[0_0_0_2px_#e8f0fe]"
                                >
                                    {languageOptions.map(lang => (
                                        <option key={lang.value} value={lang.value}>
                                            {lang.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex gap-[12px] justify-center">
                                <button
                                    className={`inline-flex items-center justify-center gap-[8px] py-[12px] px-[24px] text-[0.9375rem] font-medium rounded-[4px] cursor-pointer transition-all duration-200 font-inherit flex-1 ${(!selectedFile || isProcessing) ? 'bg-[#dadce0] text-[#80868b] cursor-not-allowed' : 'bg-[#1a73e8] text-white border-none hover:bg-[#1557b0]'}`}
                                    onClick={handleExtractText}
                                    disabled={!selectedFile || isProcessing}
                                >
                                    {isProcessing ? (
                                        <>
                                            <LoadingSpinner size="small" />
                                            Extracting... {progress}%
                                        </>
                                    ) : (
                                        <>
                                            <Type size={20} />
                                            Extract Text
                                        </>
                                    )}
                                </button>
                                {selectedFile && (
                                    <button className="inline-flex items-center justify-center gap-[8px] py-[12px] px-[24px] bg-[#ffffff] text-[#5f6368] border-[1px] border-[#dadce0] rounded-[4px] text-[0.9375rem] font-medium cursor-pointer transition-all duration-200 font-inherit hover:bg-[#f8f9fa]" onClick={handleReset}>
                                        <RefreshCw size={18} />
                                        Reset
                                    </button>
                                )}
                            </div>
                        </div>

                        {extractedText && (
                            <div className="mt-[32px] pt-[32px] border-t-[1px] border-[#dadce0]">
                                <div className="flex justify-between items-center mb-[16px] flex-wrap gap-[16px]">
                                    <h3 className="text-[1.125rem] font-medium text-[#202124] m-0">Extracted Text ({extractedText.length} characters)</h3>
                                    <div className="flex gap-[10px]">
                                        <button className="inline-flex items-center gap-[6px] py-[8px] px-[16px] rounded-[4px] text-[0.875rem] font-medium cursor-pointer border-[1px] border-[#dadce0] bg-[#ffffff] text-[#5f6368] transition-all duration-200 font-inherit hover:bg-[#f8f9fa] hover:text-[#1a73e8] hover:border-[#1a73e8]" onClick={copyToClipboard}>
                                            <Copy size={18} />
                                            Copy
                                        </button>
                                        <button className="inline-flex items-center gap-[6px] py-[8px] px-[16px] rounded-[4px] text-[0.875rem] font-medium cursor-pointer border-[1px] border-[#dadce0] bg-[#ffffff] text-[#5f6368] transition-all duration-200 font-inherit hover:bg-[#f8f9fa] hover:text-[#1a73e8] hover:border-[#1a73e8]" onClick={downloadText}>
                                            <Download size={18} />
                                            Download TXT
                                        </button>
                                    </div>
                                </div>
                                <textarea
                                    className="w-full p-[16px] border-[1px] border-[#dadce0] rounded-[8px] font-mono text-[0.9375rem] leading-[1.6] text-[#202124] bg-[#fafafa] resize-y focus:outline-none focus:border-[#1a73e8] focus:shadow-[0_0_0_2px_#e8f0fe]"
                                    value={extractedText}
                                    readOnly
                                    rows={15}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </ToolPageLayout>

            <ToolInfoStrip>
                <ToolInfoStrip.Section variant="privacy">
                    <h3><Shield size={20} />Fast &amp; Private</h3>
                    <p>This tool runs 100% locally in your browser. Your images and documents never leave your device.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><Zap size={20} />Pro Tips</h3>
                    <p>For best results, ensure the text in your image is clear and well-lit. Scanned documents work best at 300 DPI or higher.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><CheckCircle size={20} />Supported Formats</h3>
                    <ul>
                        <li>Images (JPG, PNG, WebP)</li>
                        <li>Scanned PDFs</li>
                        <li>Screenshots</li>
                    </ul>
                </ToolInfoStrip.Section>
            </ToolInfoStrip>

            <ToolSection variant="light" title="How to Extract Text from Images in 3 Steps" grid="steps">
                <ToolStepCard number={1} title="Upload Your File" description="Select an image (JPG, PNG, GIF, WebP) or PDF file containing text. Maximum file size is 10MB. Supports screenshots, photos of documents, and scanned PDFs." />
                <ToolStepCard number={2} title="Select Language" description="Choose the language of the text in your document for better accuracy. Default is English. For best results, select the correct language before extraction." />
                <ToolStepCard number={3} title="Extract & Download" description="Click extract and wait while our OCR processes your document. Copy the extracted text to clipboard or download as a TXT file for further editing." />
            </ToolSection>

            <ToolSection variant="muted" title="Powerful OCR Features" description="Our OCR engine uses advanced machine learning to accurately recognize text from images and scanned documents, even with imperfect quality." grid="features">
                <ToolFeatureCard><Globe size={28} /><h3>12+ Languages</h3><p>Support for over 12 languages including English, Spanish, French, German, Chinese, Japanese, Arabic, Hindi, and many more. Select your document's language for optimal accuracy.</p></ToolFeatureCard>
                <ToolFeatureCard><FileText size={28} /><h3>PDF Support</h3><p>Extract text from scanned PDF documents and convert them to editable text. Works with single-page and multi-page PDFs containing printed or typed text.</p></ToolFeatureCard>
                <ToolFeatureCard><Zap size={28} /><h3>High Accuracy</h3><p>AI-powered OCR engine delivers excellent accuracy on clear text. Handles various fonts, sizes, and layouts. Advanced preprocessing improves results on low-quality scans.</p></ToolFeatureCard>
                <ToolFeatureCard><Download size={28} /><h3>Multiple Output Options</h3><p>Copy extracted text to clipboard for immediate use or download as a plain text file. Edit the text directly in the results area before saving.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="muted" title="Common OCR Use Cases" grid="features">
                <ToolFeatureCard><h3>Digitize Documents</h3><p>Convert printed documents, books, and papers into searchable digital text. Archive important documents and make them searchable without manual retyping.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Extract Screenshot Text</h3><p>Extract text from screenshots of emails, messages, or web pages. Copy text from images when regular copy-paste isn't available.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Process Receipts</h3><p>Extract information from receipts, invoices, and bills for expense tracking. Digitize financial documents for accounting and bookkeeping purposes.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Lecture Notes</h3><p>Convert photos of whiteboards, slides, or handwritten notes into editable text. Great for students digitizing lecture materials or meeting notes.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="light" title="Frequently Asked Questions" grid="none">
                <div className="flex flex-col gap-[20px] mb-[32px]">
                    <ToolFeatureCard variant="faq"><h3>What is OCR and how does it work?</h3><p>OCR (Optical Character Recognition) is technology that converts images of text into editable, searchable text. Our tool uses AI to analyze image patterns and recognize characters, words, and sentences with high accuracy.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>What languages are supported?</h3><p>We support over 12 languages including English, Spanish, French, German, Chinese, Japanese, Arabic, Hindi, Russian, Korean, and many more. Select your document's language before extraction for best results.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>Can OCR read handwritten text?</h3><p>OCR works best on printed or typed text. Handwriting recognition accuracy varies based on legibility. Clear, well-spaced handwriting may work, but printed text provides the most reliable results.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>What affects OCR accuracy?</h3><p>Accuracy depends on image quality, text clarity, font type, and language complexity. Best results come from high-resolution images with clear, well-lit text. Blurry or low-contrast images may produce errors.</p></ToolFeatureCard>
                </div>
                <ToolFaqCta prompt="More questions?" to="/faq" label="Visit our FAQ page" />
            </ToolSection>

            <ToolSection variant="muted" title="Related Text &amp; Document Tools" grid="related">
                <ToolRelatedCard to="/tools/compare-text" icon={<FileText size={24} />} title="Text Comparison" description="Compare extracted text with original documents" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/barcode-scanner" icon={<Scan size={24} />} title="Barcode Scanner" description="Scan and decode barcodes from images" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/compress-pdf-images" icon={<Download size={24} />} title="File Size Reducer" description="Compress extracted or scanned documents" linkLabel="Try Tool" />
            </ToolSection>
        </>
    );
};

export default OCRToolPage;
